import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ETABLISSEMENT_TYPES } from "../../content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
  prenom: "",
  nom: "",
  telephone: "",
  email: "",
  type_etablissement: "",
  ville: "",
};

const fieldClass =
  "w-full bg-white text-[#1A2E2C] rounded-xl px-5 py-4 border border-transparent focus:border-[#46c3b2] focus:ring-2 focus:ring-[#46c3b2]/30 transition-all outline-none font-medium placeholder:text-[#1A2E2C]/40";

export default function LeadForm() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);

  const onChange = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.type_etablissement) {
      toast.error("Merci de sélectionner un type d'établissement.");
      return;
    }
    if (!consent) {
      toast.error("Merci d'accepter la politique de confidentialité.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      setForm(initialState);
      setConsent(false);
      toast.success("Demande reçue. Un expert vous rappelle sous 24h.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Impossible d'envoyer votre demande. Vérifiez vos coordonnées."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="lead-form"
      data-testid="lead-form-section"
      className="relative text-white met-section overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #003242 0%, #005870 55%, #0095b4 100%)",
      }}
    >
      {/* Decorative accents */}
      <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-[#88d2e7]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#005870]/30 blur-3xl pointer-events-none" />

      <div className="met-container relative">
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 max-w-6xl mx-auto">
            <div className="lg:col-span-5">
              <div className="text-[#88d2e7] text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-4">
                Contact
              </div>
              <h2
                data-testid="lead-form-title"
                className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] text-balance"
              >
                Un expert vous rappelle <span className="text-[#88d2e7]">sous 24h.</span>
              </h2>
              <p className="mt-6 text-white/80 text-base md:text-lg font-medium leading-relaxed">
                Laissez vos coordonnées — pas de démarchage, juste un regard expert sur votre situation.
              </p>
            </div>

            <div className="lg:col-span-7">
              {submitted ? (
                <div
                  data-testid="lead-form-success"
                  className="bg-white text-[#1A2E2C] rounded-2xl p-8 md:p-10 flex flex-col items-start"
                >
                  <div className="w-14 h-14 rounded-full bg-[#88d2e7]/30 text-[#005870] flex items-center justify-center mb-5">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-extrabold mb-2">{`Merci, c'est noté.`}</h3>
                  <p className="text-[#1A2E2C]/70 font-medium">
                    Un expert MET vous recontacte sous 24h ouvrées. À très vite.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="lead-form-reset"
                    className="mt-6 inline-flex items-center gap-2 text-[#005870] font-bold hover:underline"
                  >
                    Envoyer une autre demande →
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="lead-form"
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  noValidate
                >
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/70">Prénom</span>
                    <input
                      data-testid="lead-form-firstname"
                      required
                      value={form.prenom}
                      onChange={onChange("prenom")}
                      type="text"
                      placeholder="Marie"
                      className={fieldClass}
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/70">Nom</span>
                    <input
                      data-testid="lead-form-lastname"
                      required
                      value={form.nom}
                      onChange={onChange("nom")}
                      type="text"
                      placeholder="Dupont"
                      className={fieldClass}
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/70">Téléphone</span>
                    <input
                      data-testid="lead-form-phone"
                      required
                      value={form.telephone}
                      onChange={onChange("telephone")}
                      type="tel"
                      placeholder="06 12 34 56 78"
                      className={fieldClass}
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/70">Email</span>
                    <input
                      data-testid="lead-form-email"
                      required
                      value={form.email}
                      onChange={onChange("email")}
                      type="email"
                      placeholder="contact@monrestaurant.fr"
                      className={fieldClass}
                    />
                  </label>

                  <label className="flex flex-col gap-2 md:col-span-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/70">{`Type d'établissement`}</span>
                    <Select
                      value={form.type_etablissement}
                      onValueChange={(v) => setForm((s) => ({ ...s, type_etablissement: v }))}
                    >
                      <SelectTrigger
                        data-testid="lead-form-type"
                        className={`${fieldClass} h-auto justify-between data-[placeholder]:text-[#1A2E2C]/40`}
                      >
                        <SelectValue placeholder="Choisir..." />
                      </SelectTrigger>
                      <SelectContent>
                        {ETABLISSEMENT_TYPES.map((t) => (
                          <SelectItem
                            key={t}
                            value={t}
                            data-testid={`lead-form-type-option-${t}`}
                          >
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>

                  <label className="flex flex-col gap-2 md:col-span-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/70">Ville</span>
                    <input
                      data-testid="lead-form-city"
                      required
                      value={form.ville}
                      onChange={onChange("ville")}
                      type="text"
                      placeholder="Lyon"
                      className={fieldClass}
                    />
                  </label>

                  <div className="md:col-span-2 mt-2 space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        data-testid="lead-form-consent"
                        className="mt-1 h-4 w-4 accent-[#46c3b2] shrink-0 cursor-pointer"
                      />
                      <span className="text-xs md:text-sm text-white/80 font-medium leading-relaxed">
                        {`J'accepte que mes données soient traitées par MET France pour répondre à ma demande, conformément à la `}
                        <Link
                          to="/politique-de-confidentialite"
                          data-testid="lead-form-privacy-link"
                          className="underline underline-offset-2 text-white hover:text-[#88d2e7] font-semibold"
                        >
                          politique de confidentialité
                        </Link>
                        {`.`}
                      </span>
                    </label>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="lead-form-submit"
                      className="group w-full inline-flex items-center justify-center gap-2 bg-white text-[#005870] hover:bg-[#88d2e7] hover:text-[#003242] font-bold text-base px-8 py-5 rounded-full transition-all duration-300 shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {submitting ? "Envoi en cours..." : "Je veux être rappelé(e) par un expert"}
                      {!submitting && (
                        <svg className="transition-transform group-hover:translate-x-1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14M13 6l6 6-6 6"/>
                        </svg>
                      )}
                    </button>
                    <p
                      data-testid="lead-form-microcopy"
                      className="text-xs text-white/60 text-center font-medium"
                    >
                      Données utilisées uniquement pour traiter votre demande. Aucun spam.
                    </p>
                  </div>
                </form>
              )}
            </div>
        </div>
      </div>
    </section>
  );
}
